import { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import PageSEO from "@/components/PageSEO";
import { breadcrumb } from "@/lib/schemas";
import ScheduleNav from "@/components/ScheduleNav";
import ChoosePathModal from "@/components/ChoosePathModal";
import { Button } from "@/components/ui/button";
import { CheckCircle, Code, Globe, Zap, ArrowLeft } from "lucide-react";

const Day1 = () => {
  const [modalOpen, setModalOpen] = useState(false);

  const walkAwayWith = [
    { text: "Your personal domain connected", icon: Globe },
    { text: "A simple real estate homepage live", icon: Zap },
    { text: "A website setup you can keep long-term", icon: CheckCircle },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#fffcf2] font-sans">
      <PageSEO
        title="Day 1 · Website Foundations | Tech Founder Summit"
        description="Day 1 of the Tech Founder Summit. Connect your domain, launch a simple real estate homepage, and own your website foundation long-term."
        canonical="https://marquettabryant.com/day-1"
        jsonLd={[breadcrumb([{name:"Home",path:"/"},{name:"Tech Founder Summit",path:"/tech-founder-summit"},{name:"Day 1",path:"/day-1"}])]}
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#2e4053] to-[#1a1a2e] pt-8 pb-16 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-64 h-64 bg-[#1e90ff]/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-20 w-80 h-80 bg-[#d8239b]/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          {/* Back Link */}
          <a 
            href="https://marquettabryant.com/tech-founder-summit" 
            className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            ← Back to Summit Home
          </a>
          
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1e90ff]/20 rounded-full mb-6">
              <Code className="w-5 h-5 text-[#1e90ff]" />
              <span className="text-[#1e90ff] font-semibold text-sm">DAY 1 • FEB 10</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Website Independence Lab
            </h1>
            
            <p className="text-xl md:text-2xl text-white/80 mb-10 max-w-2xl mx-auto">
              Leave with a website you own and can take anywhere.
            </p>
            
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-[#d8239b] to-[#ff69b4] hover:from-[#d8239b]/90 hover:to-[#ff69b4]/90 text-white font-bold px-10 py-7 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
              onClick={() => setModalOpen(true)}
            >
              Choose Your Path
            </Button>
          </div>
        </div>
      </section>
      
      <ScheduleNav />
      
      <main className="pt-12 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            {/* Outcome Badge */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-10 mb-8">
              <div className="bg-gradient-to-r from-[#d8239b]/10 to-[#ff69b4]/10 rounded-2xl p-6 border border-[#d8239b]/20">
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-3 py-1 bg-[#d8239b] text-white text-xs font-bold rounded-full">OUTCOME</span>
                </div>
                <p className="text-lg md:text-xl text-[#2e4053] font-medium">
                  Leave with a website you own, control, and can take anywhere.
                </p>
              </div>
              
              {/* What We Build */}
              <div className="mt-8">
                <h2 className="text-2xl font-bold text-[#2e4053] mb-4 flex items-center gap-2">
                  <span className="w-8 h-1 bg-gradient-to-r from-[#d8239b] to-[#ff69b4] rounded-full"></span>
                  What We Build Live
                </h2>
                <p className="text-[#2e4053]/80 leading-relaxed text-lg">
                  Most agents are renting their online presence through their brokerage.
                  <span className="font-semibold text-[#d8239b]"> This day is about ownership.</span>
                </p>
              </div>
            </div>

            {/* Walk Away With Card */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-10 mb-8">
              <h3 className="text-xl font-bold text-[#2e4053] mb-6">You Will Walk Away With</h3>
              <div className="space-y-4">
                {walkAwayWith.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 p-4 bg-[#fffcf2] rounded-xl border border-gray-100">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#d8239b] to-[#ff69b4] flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-[#2e4053] font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Options Card */}
            <div className="bg-[#fffcf2] rounded-3xl shadow-lg border border-[#d8239b]/10 p-8 mb-10">
              <h3 className="font-bold text-[#2e4053] mb-4 text-lg">Two options:</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                  <span className="text-[#1e90ff] font-bold text-sm">OPTION 1</span>
                  <p className="text-[#2e4053]/80 mt-1">Improve what you have inside company limits</p>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                  <span className="text-[#d8239b] font-bold text-sm">OPTION 2</span>
                  <p className="text-[#2e4053]/80 mt-1">Build your own independent site that links back</p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center mb-10">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-[#d8239b] to-[#ff69b4] hover:from-[#d8239b]/90 hover:to-[#ff69b4]/90 text-white font-bold px-12 py-7 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
                onClick={() => setModalOpen(true)}
              >
                Choose Your Path
              </Button>
            </div>

            {/* Bottom Back Link */}
            <div className="text-center">
              <a 
                href="https://marquettabryant.com/tech-founder-summit" 
                className="inline-flex items-center gap-2 text-[#2e4053]/70 hover:text-[#d8239b] transition-colors font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                ← Back to Summit Home
              </a>
            </div>
          </div>
        </div>
      </main>

      <ChoosePathModal open={modalOpen} onOpenChange={setModalOpen} />
    </div>
  );
};

export default Day1;
